/**
 * Which API this console talks to.
 *
 * **Derived from the hostname the page was served on, never compiled in.** There is one
 * bundle for every deployment, so there is no such thing as "the pre-prod build" that
 * could be uploaded to prod: a page served from `admin.preprod.tasks4staff.com` talks to
 * `api.preprod.tasks4staff.com`, and one served from `admin.tasks4staff.com` talks to
 * `api.tasks4staff.com`, because that is the hostname scheme docs/ENVIRONMENTS.md §8 fixes.
 *
 * **One scheme is recognised.** The rename to Tasks4Staff moved every surface to the
 * project's own zone `tasks4staff.com`; the `mdemo.app` addresses this console and the API
 * also answered on were retired 2026-08-23 (docs/DECISIONS.md, "Public hostnames — one
 * label per environment under `mdemo.app`"). Each pattern maps a console to the API of
 * **its own** environment: crossing them would point a pre-prod console at a production
 * API.
 *
 * That is only half the guarantee. The word in the masthead comes from the API's own
 * `/health` (docs/ux/PLATFORM-ADMIN.md §5.1), so if this derivation is ever wrong the
 * screen says what it is actually pointed at rather than what it believes.
 *
 * `VITE_API_BASE_URL` overrides it, which is how a local console reaches something other
 * than the local Worker. It is a URL, not a secret. Note that pointing a *local* console
 * at a *deployed* API also needs that console's origin on the API's CORS allowlist, and
 * it deliberately is not there (`apps/api/src/middleware/cors.ts`): each deployment
 * admits its own consoles and nothing else.
 */

/**
 * The published console hostnames, each paired with the API hostname that belongs with it.
 *
 * A list of exact patterns rather than one clever expression: every entry here is a
 * hostname somebody deliberately published, and an unrecognised address must fall through
 * to the local API rather than be pattern-matched into a deployed one. That matters more
 * on `tasks4staff.com` than it did before — every tenant will sit one label below the same
 * apex, so `<tenant>.tasks4staff.com` must not be mistaken for a console.
 */
const CONSOLE_HOSTNAMES: readonly { readonly console: RegExp; readonly api: string }[] = [
  // Tasks4Staff, on its own zone. One label per surface; the environment is a suffix
  // inside that label, so every hostname stays under the free wildcard certificate.
  { console: /^admin\.tasks4staff\.com$/, api: 'https://api.tasks4staff.com' },
  { console: /^admin\.preprod\.tasks4staff\.com$/, api: 'https://api.preprod.tasks4staff.com' },
]

/** Where `pnpm --filter @t4s/api dev` serves the API. */
export const LOCAL_API_URL = 'http://localhost:8787'

export type PageLocation = {
  readonly hostname: string
}

/**
 * The API origin for a page served at `location`, with no trailing slash.
 *
 * An explicit override wins. A published console hostname resolves to its own
 * environment's API. Anything else — `localhost`, a preview, a tenant, a hostname nobody
 * planned — falls back to the local API rather than guessing at a deployed one, because a
 * console that silently reached a real estate from an unrecognised address is the mistake
 * §5.1 exists to prevent.
 */
export function resolveApiBaseUrl(location: PageLocation, override?: string): string {
  const configured = override?.trim() ?? ''
  if (configured !== '') {
    return configured.replace(/\/+$/, '')
  }

  return (
    CONSOLE_HOSTNAMES.find((candidate) => candidate.console.test(location.hostname))?.api ??
    LOCAL_API_URL
  )
}
